import { FC, useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Checkbox from 'components/Checkbox/Checkbox';
import { CheckbokItem } from 'components/FilterComponents/CheckList/CheckbokItem';
import { cn } from 'components/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  UIButton
} from 'components/ui';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  renderValue?: (selected: string[]) => string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  dataTestId?: string;
  boxDataTestId?: string;
  listDataTestId?: string;
  itemDataTestId?: string;
}

export const MultiSelect: FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select',
  renderValue,
  showSelectAll = false,
  selectAllLabel = 'Select all',
  className,
  buttonClassName,
  iconClassName,
  contentClassName,
  disabled = false,
  dataTestId,
  boxDataTestId,
  listDataTestId,
  itemDataTestId
}) => {
  const [open, setOpen] = useState(false);

  const displayText = useMemo(() => {
    if (!value || value.length === 0) return placeholder;
    if (renderValue) return renderValue(value);
    if (value.length === options.length) return 'All';
    if (value.length === 1) {
      return options.find((o) => o.value === value[0])?.label || value[0];
    }
    return value
      .map((v) => options.find((o) => o.value === v)?.label || v)
      .join(', ');
  }, [value, placeholder, renderValue, options]);

  const allSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((option) => value.includes(option.value)),
    [options, value]
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      const newSelection = value.filter(
        (v) => !options.some((o) => o.value === v)
      );
      onChange(newSelection);
    } else {
      const valuesToAdd = options
        .map((o) => o.value)
        .filter((v) => !value.includes(v));
      onChange([...value, ...valuesToAdd]);
    }
  }, [allSelected, options, value, onChange]);

  const handleToggle = useCallback(
    (toggleValue: string) => {
      const newSelection = value.includes(toggleValue)
        ? value.filter((v) => v !== toggleValue)
        : [...value, toggleValue];
      onChange(newSelection);
    },
    [value, onChange]
  );

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <UIButton
            variant="outline"
            disabled={disabled}
            data-testid={boxDataTestId ?? dataTestId}
            className={cn(
              'shadow-none group h-10 justify-between bg-white p-s text-base-foreground font-normal w-full hover:bg-base-accent focus:bg-base-accent',
              !value.length && 'text-base-mutedForeground',
              buttonClassName
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {displayText}
              </span>
              <FontAwesomeIcon
                icon="fa-solid fa-chevron-down"
                className={cn(
                  'text-base-foreground transition-transform group-data-[state=open]:rotate-180 text-[#404040]',
                  iconClassName
                )}
              />
            </div>
          </UIButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          className={cn(
            'w-[--radix-popover-trigger-width] !p-[4px] rounded-md bg-white text-base-foreground font-normal z-1000',
            contentClassName
          )}
        >
          <div className="flex flex-col text-xs">
            {showSelectAll && (
              <div className="pb-1 border-b border-base-input">
                <Checkbox
                  id="multi-select-all"
                  className="[&[data-state=checked]]:bg-indigo-500 [&[data-state=indeterminate]]:bg-indigo-500"
                  label={selectAllLabel}
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
              </div>
            )}
            <div
              className="flex flex-col gap-0 max-h-[200px] overflow-y-auto"
              data-testid={listDataTestId}
            >
              {options.length > 0 ? (
                options.map((item) => (
                  <CheckbokItem
                    key={item.value}
                    item={item}
                    selectedItems={value}
                    onChange={handleToggle}
                    dataTestId={itemDataTestId}
                  />
                ))
              ) : (
                <div className="text-center text-xs text-base-foreground/50 py-2">
                  No options available
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
